import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Info } from "components/icons/Info";
import { Field, FieldProps } from "formik";
import { ReactNode, useRef } from "react";

export interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  name?: string;
  autoResize?: boolean;
  block?: boolean;
  note?: ReactNode;
  tooltipText?: string | ReactNode;
  size?: "small" | "regular";
  fillParentHeight?: boolean;
  isCopyable?: boolean;
}

export const TextArea = ({
  label,
  autoResize = true,
  block = false,
  note,
  tooltipText,
  size = "regular",
  fillParentHeight = false,
  disabled,
  isCopyable = false,
  ...props
}: TextAreaProps) => {
  const textArea = useRef<null | HTMLTextAreaElement>(null);

  return (
    <div
      className={classNames({
        "t-w-full": block,
        "t-h-full": fillParentHeight,
      })}
    >
      <Field name={props.name} {...props}>
        {({ field, form: { touched, errors } }: FieldProps) => {
          const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            field.onChange(e);
            props.onChange?.(e);
          };
          const hasError = touched[field.name] && errors[field.name];
          const displayValue =
            props.value !== undefined ? props.value : field.value;

          return (
            <div className={classNames({ "t-h-full": fillParentHeight })}>
              {Boolean(label) && (
                <div className="t-flex t-items-center">
                  <Label required={props?.required} htmlFor={props.name}>
                    {label}
                  </Label>
                  {tooltipText && (
                    <div className="t-ml-1 t-pb-1.5">
                      <ToolTip text={tooltipText} side="right">
                        <span>
                          <Info />
                        </span>
                      </ToolTip>
                    </div>
                  )}
                </div>
              )}

              <div className={classNames({ "t-h-full": fillParentHeight })}>
                <div
                  data-value={displayValue}
                  className={classNames(
                    "t-col-start-[1] t-w-full after:t-invisible after:t-col-start-[1] after:t-whitespace-pre-wrap",
                    {
                      "t-grid after:t-content-[attr(data-value)] after:t-row-start-[1] after:t-break-words after:t-min-h-full after:t-min-w-0 t-min-h-full":
                        autoResize && fillParentHeight,
                      "after:t-p-3":
                        autoResize && fillParentHeight && size === "regular",
                      "after:t-p-2":
                        autoResize && fillParentHeight && size === "small",

                      "after:t-row-start-[2]": !fillParentHeight,
                      "t-inline-grid after:t-content-[attr(data-value)] after:t-word-break":
                        autoResize && !fillParentHeight,
                    }
                  )}
                >
                  <textarea
                    id={field.name}
                    {...{ ...field, ...props }}
                    readOnly={!!disabled}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : undefined}
                    onMouseDown={(e) => {
                      if (disabled && !isCopyable) e.preventDefault();
                      props.onMouseDown?.(e);
                    }}
                    disabled={disabled}
                    onChange={onChange}
                    ref={textArea}
                    className={classNames(
                      "t-col-start-[1] t-w-full t-resize-none t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-text-body-sm t-text-text-100 t-transition-all disabled:t-cursor-not-allowed disabled:t-opacity-60",
                      {
                        "focus:t-border-purple focus:t-bg-surface-transparent focus:t-outline-none hover:t-border-purple-30 hover:t-bg-surface-grey":
                          !disabled,
                        "t-row-start-[1] t-min-h-full t-min-w-0":
                          fillParentHeight,
                        "t-row-start-[2]": !fillParentHeight,
                        "t-border-red focus:t-border-red": hasError,
                        "t-p-3": size === "regular",
                        "t-p-2": size === "small",
                      }
                    )}
                  />
                </div>
              </div>
              {hasError && (
                <HelperText variant="error">
                  {/* @ts-ignore */}
                  {errors[field.name]}
                </HelperText>
              )}
              {note && <HelperText variant="default">{note}</HelperText>}
            </div>
          );
        }}
      </Field>
    </div>
  );
};
